/* Credits: the balance in the top bar + the Credits modal (packages, buy). Shared wallet (M4). */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getBalance, getPackages, startCheckout } from '../api/wallet.js';
import { Button, Icon, Modal, Skeleton, Toast } from '../ui/index.jsx';
import { useSession } from './session.jsx';
import { Trans, useTranslation } from 'react-i18next';
import i18n from '../i18n/index.js';

const Ctx = createContext(null);

export function WalletProvider({ children }) {
  const { signedIn } = useSession();
  const [balance, setBalance] = useState(null);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const last = useRef(null);
  const refresh = useCallback(async () => {
    const r = await getBalance();
    if (!(r.ok && r.data && r.data.data)) return;
    const b = r.data.data.balance;
    // Credits arrived (desktop "credits added" toast): a purchase, a school assignment or a refund raised the balance.
    if (last.current != null && b > last.current) {
      const n = b - last.current;
      setNotice({ tone: 'green', title: i18n.t('bv:wallet.added', { credits: i18n.t('bv:common.credits', { count: n }) }), sub: i18n.t('bv:wallet.nowHave', { count: b }) });
    }
    last.current = b;
    setBalance(b);
  }, []);
  useEffect(() => {
    if (!signedIn) { setBalance(null); last.current = null; return undefined; }
    refresh();
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [signedIn, refresh]);
  // Back from Stripe: ?payment=success|cancelled
  useEffect(() => {
    const p = new URLSearchParams(location.search).get('payment');
    if (!p) return;
    setNotice(p === 'success' ? { tone: 'green', title: i18n.t('bv:wallet.thanks'), sub: i18n.t('bv:wallet.adding') } : { tone: 'yellow', title: i18n.t('bv:wallet.cancelled'), sub: i18n.t('bv:wallet.noMoney') });
    const u = new URL(location.href);
    u.searchParams.delete('payment'); u.searchParams.delete('session_id');
    history.replaceState(null, '', u.pathname + (u.search ? u.search : '') + u.hash);
    if (p === 'success') { refresh(); setTimeout(refresh, 4000); setTimeout(refresh, 12000); }
    const t = setTimeout(() => setNotice(null), 8000);
    return () => clearTimeout(t);
  }, [refresh]);
  useEffect(() => { if (!notice) return undefined; const t = setTimeout(() => setNotice(null), 8000); return () => clearTimeout(t); }, [notice]);
  const setBal = useCallback((b) => { last.current = b; setBalance(b); }, []);
  const value = { balance, refresh, setBalance: setBal, openCredits: () => setOpen(true) };
  return (
    <Ctx.Provider value={value}>
      {children}
      <CreditsModal open={open} onClose={() => setOpen(false)} balance={balance} />
      {notice && (
        <div className="bv-wallet-notice"><Toast icon={notice.tone === 'green' ? 'check' : 'sparkle'} color={notice.tone} title={notice.title} subtitle={notice.sub} /></div>
      )}
    </Ctx.Provider>
  );
}
export const useWallet = () => useContext(Ctx) || { balance: null, refresh: () => {}, setBalance: () => {}, openCredits: () => {} };

const money = (cents, cur) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: (cur || 'usd').toUpperCase() }).format(cents / 100);

function CreditsModal({ open, onClose, balance }) {
  const { t } = useTranslation('bv');
  const [packages, setPackages] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    if (!open || packages) return;
    getPackages().then((r) => setPackages(r.ok && r.data && r.data.data ? r.data.data.packages : []));
  }, [open, packages]);
  const buy = async (p) => {
    setBusy(p.id); setMsg(null);
    const r = await startCheckout(p.id);
    setBusy(null);
    if (r.ok && r.data && r.data.data && r.data.data.checkoutUrl) { location.href = r.data.data.checkoutUrl; return; }
    setMsg(r.status === 503 ? t('wallet.off') : r.error || t('wallet.checkoutFailed'));
  };
  return (
    <Modal open={open} title={t('wallet.title')} onClose={onClose} width={560}>
      <div className="bv-credits-modal" data-testid="credits-modal">
        <p className="bv-credits-modal__balance"><Icon name="sparkle" size={22} /> <Trans t={t} i18nKey="wallet.youHave" count={balance == null ? 0 : balance} values={{ count: balance == null ? '…' : balance }} components={{ b: <b /> }} /></p>
        <p className="bv-small bv-muted">{t('wallet.hint')}</p>
        <div className="bv-credits-modal__list">
          {!packages ? [0, 1, 2].map((i) => <Skeleton key={i} height={72} radius={18} />) : packages.map((p) => (
            <div key={p.id} className="bv-credits-modal__pkg">
              <div>
                <div className="bv-credits-modal__name">{p.name}</div>
                <div className="bv-small bv-muted">{t('common.credits', { count: p.credits })} · {t('wallet.perCredit', { price: money(p.price_cents / Math.max(1, p.credits), p.currency) })}</div>
              </div>
              <Button variant="purple" icon="sparkle" disabled={!!busy} onClick={() => buy(p)}>{busy === p.id ? t('common.opening') : money(p.price_cents, p.currency)}</Button>
            </div>
          ))}
        </div>
        {msg && <p className="bv-error-text" role="alert">{msg}</p>}
      </div>
    </Modal>
  );
}
